import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import SectionCard from '../../components/SectionCard';
import FilterToolbar from '../../components/FilterToolbar';
import InvDataGrid from '../../components/InvDataGrid';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { InvTextField, InvSelect } from '../../components/InvField';
import MaterialMasterShell from '../../components/master/MaterialMasterShell';
import {
  getMaterialGroups, createMaterialGroup, updateMaterialGroup, deleteMaterialGroup,
} from '../../api/materialGroup';
import { MASTER_PAGE_SIZE, STATUS_FILTER_OPTIONS } from '../../constants';
import {
  normalizeMaterialGroupList, normalizeMaterialGroup, materialGroupRowId,
  resolveMaterialGroupParentLabel, toInventoryPage,
  collectDescendantIds, hierarchicalParentSelectOptions,
} from '../../utils/masterEntity';
import { formatDate } from '../../utils';
import { IPD_COLORS, cardSx, inputSx, formRowSx, switchSx } from '../../theme';
import useMasterList from '../../hooks/useMasterList';

const GROUP_KEYS = {
  idKey: 'material_group_id',
  nameKey: 'group_name',
  codeKey: 'group_code',
  parentKey: 'parent_group_id',
};

const EMPTY_FORM = {
  group_code: '',
  group_name: '',
  parent_group_id: '',
  description: '',
};

const EDIT_FORM = {
  group_name: '',
  parent_group_id: '',
  description: '',
  is_active: true,
};

const ADD_FORM_ROW_SX = {
  ...formRowSx,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: 2,
  mb: 0,
};

const ADD_FIELD_CODE_SX = { flex: '0 1 150px', minWidth: 130, maxWidth: 180 };
const ADD_FIELD_NAME_SX = { flex: '1 1 200px', minWidth: 160 };
const ADD_FIELD_PARENT_SX = { flex: '1 1 240px', minWidth: 200 };
const ADD_FIELD_DESC_SX = { flex: '2 1 260px', minWidth: 200 };

function SwitchField({ name, label, control: formControl }) {
  return (
    <Controller
      name={name}
      control={formControl}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Switch
              checked={!!field.value}
              onChange={e => field.onChange(e.target.checked)}
              sx={switchSx}
            />
          }
          label={label}
          sx={{ color: IPD_COLORS.textPrimary, m: 0 }}
        />
      )}
    />
  );
}

function buildListParams({ page, search, statusFilter, pageSize }) {
  const params = { limit: pageSize, offset: page * pageSize };
  if (search.trim()) params.search = search.trim();
  if (statusFilter === 'active') params.is_active = true;
  if (statusFilter === 'inactive') params.is_active = false;
  return params;
}

function buildCreateBody(data) {
  return {
    group_code: data.group_code.trim().toUpperCase(),
    group_name: data.group_name.trim(),
    parent_group_id: data.parent_group_id || null,
    description: data.description?.trim() || '',
    group_payload: {},
    is_active: true,
  };
}

function buildUpdateBody(data) {
  return {
    group_name: data.group_name.trim(),
    parent_group_id: data.parent_group_id || null,
    description: data.description?.trim() || '',
    is_active: data.is_active !== false,
  };
}

function mapRowToEditForm(row) {
  const parentId = row?.parent_group_id ?? row?.parent_group?.id ?? '';
  return {
    group_name: row?.group_name ?? '',
    parent_group_id: parentId === '' || parentId == null ? '' : String(parentId),
    description: row?.description ?? '',
    is_active: row?.is_active !== false,
  };
}

export default function MaterialGroupList() {
  const list = useMasterList({ pageSize: MASTER_PAGE_SIZE });
  const {
    items, setItems, totalCount, setTotalCount,
    loading, setLoading, error, setError,
    page, setPage, searchInput, setSearchInput,
    search, statusFilter, setStatusFilter,
    resetFilters, pageSize,
  } = list;

  const [allGroups, setAllGroups] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: EMPTY_FORM });
  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm({ defaultValues: EDIT_FORM });

  const hasActiveFilters = Boolean(search.trim() || statusFilter);

  const loadAllGroups = useCallback(async () => {
    try {
      const data = await getMaterialGroups({ limit: 200, is_active: true });
      setAllGroups(normalizeMaterialGroupList(data));
    } catch {
      setAllGroups([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getMaterialGroups(buildListParams({ page, search, statusFilter, pageSize }));
      const { items: fetched, total } = toInventoryPage(raw, { page, pageSize });
      setItems(fetched.map(normalizeMaterialGroup));
      setTotalCount(total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, pageSize, setLoading, setError, setItems, setTotalCount]);

  useEffect(() => { loadAllGroups(); }, [loadAllGroups]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editTarget) resetEdit(mapRowToEditForm(editTarget));
  }, [editTarget, resetEdit]);

  const parentOpts = useMemo(
    () => hierarchicalParentSelectOptions(allGroups, GROUP_KEYS),
    [allGroups],
  );

  const editId = editTarget ? materialGroupRowId(editTarget) : null;
  const editParentOpts = useMemo(() => {
    if (!editId) return parentOpts;
    const excludeIds = collectDescendantIds(allGroups, editId, GROUP_KEYS);
    return hierarchicalParentSelectOptions(allGroups, { ...GROUP_KEYS, excludeIds: [...excludeIds] });
  }, [allGroups, editId, parentOpts]);

  const columns = useMemo(() => [
    { field: 'group_code', headerName: 'Code', width: 110 },
    { field: 'group_name', headerName: 'Name', flex: 1, minWidth: 150 },
    {
      field: 'parent_group_id',
      headerName: 'Parent',
      width: 150,
      valueGetter: (p) => resolveMaterialGroupParentLabel(p.row, allGroups),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => p.value !== false
        ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.success.text, fontSize: 18 }} />
        : <CancelIcon sx={{ color: IPD_COLORS.error.text, fontSize: 18 }} />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit material group">
            <IconButton size="small" onClick={() => setEditTarget(p.row)} sx={{ color: IPD_COLORS.primary }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.is_active !== false && (
            <Tooltip title="Deactivate material group">
              <IconButton size="small" onClick={() => setDeactivateTarget(p.row)} sx={{ color: IPD_COLORS.error.text }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [allGroups]);

  function closeEditModal() {
    setEditTarget(null);
    resetEdit(EDIT_FORM);
  }

  async function onSubmit(data) {
    setSaving(true);
    try {
      await createMaterialGroup(buildCreateBody(data));
      reset(EMPTY_FORM);
      toast.success('Material group created');
      await Promise.all([loadAllGroups(), load()]);
    } catch (e) {
      toast.error(e?.message || 'Failed to create material group');
    } finally {
      setSaving(false);
    }
  }

  async function onEditSubmit(data) {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await updateMaterialGroup(materialGroupRowId(editTarget), buildUpdateBody(data));
      closeEditModal();
      toast.success('Material group updated');
      await Promise.all([loadAllGroups(), load()]);
    } catch (e) {
      toast.error(e?.message || 'Failed to update material group');
    } finally {
      setEditSaving(false);
    }
  }

  async function confirmDeactivate() {
    setDeactivating(true);
    try {
      await deleteMaterialGroup(materialGroupRowId(deactivateTarget));
      setDeactivateTarget(null);
      toast.success('Material group deactivated');
      await Promise.all([loadAllGroups(), load()]);
    } catch (e) {
      toast.error(e?.message || 'Failed to deactivate material group');
      setDeactivateTarget(null);
    } finally {
      setDeactivating(false);
    }
  }

  return (
    <MaterialMasterShell
      title="Material Group Master"
      description="Create and manage material groups to organize inventory items in a hierarchy."
      badge={`${totalCount} ${totalCount === 1 ? 'group' : 'groups'}`}
    >
      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by name or code..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Status"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          options={STATUS_FILTER_OPTIONS}
        />
        <FilterToolbar.Reset onClick={resetFilters} disabled={!hasActiveFilters} />
      </FilterToolbar>

      <SectionCard title="Add Material Group" sx={{ mb: 2.5 }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={ADD_FORM_ROW_SX}>
            <Box sx={ADD_FIELD_CODE_SX}>
              <Controller
                name="group_code"
                control={control}
                rules={{ required: 'Group code is required' }}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label="Group Code *"
                    error={errors.group_code}
                    onChange={e => field.onChange(e.target.value.toUpperCase())}
                  />
                )}
              />
            </Box>
            <Box sx={ADD_FIELD_NAME_SX}>
              <Controller
                name="group_name"
                control={control}
                rules={{ required: 'Group name is required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Group Name *" error={errors.group_name} />
                )}
              />
            </Box>
            <Box sx={ADD_FIELD_PARENT_SX}>
              <Controller
                name="parent_group_id"
                control={control}
                render={({ field }) => (
                  <InvSelect
                    label="Parent Group"
                    value={field.value}
                    onChange={field.onChange}
                    options={parentOpts}
                  />
                )}
              />
            </Box>
            <Box sx={ADD_FIELD_DESC_SX}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Description" />
                )}
              />
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              <InvButton type="submit" loading={saving} startIcon={<AddIcon />} sx={{ minWidth: 190, whiteSpace: 'nowrap' }}>
                Add Material Group
              </InvButton>
            </Box>
          </Box>
        </Box>
      </SectionCard>

      <SectionCard title="Material Groups" noPadding>
        <InvDataGrid
          embedded
          fillHeight
          rows={items}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          getRowId={materialGroupRowId}
          emptyMessage="No material groups yet"
          emptyDescription="Use the form above to create your first material group."
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          pageSizeOptions={[pageSize]}
        />
      </SectionCard>

      <Dialog
        open={Boolean(editTarget)}
        onClose={editSaving ? undefined : closeEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: cardSx }}
      >
        <DialogTitle sx={{
          color: IPD_COLORS.textPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}>
          Edit Material Group
          <IconButton onClick={closeEditModal} disabled={editSaving} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            id="edit-material-group-form"
            onSubmit={handleEditSubmit(onEditSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
          >
            <InvTextField label="Group Code" value={editTarget?.group_code || ''} disabled />
            <Controller
              name="group_name"
              control={editControl}
              rules={{ required: 'Group name is required' }}
              render={({ field }) => (
                <InvTextField {...field} label="Group Name *" error={editErrors.group_name} />
              )}
            />
            <Controller
              name="parent_group_id"
              control={editControl}
              render={({ field }) => (
                <InvSelect
                  label="Parent Group (optional)"
                  value={field.value}
                  onChange={field.onChange}
                  options={editParentOpts}
                  helperText="Leave empty for a top-level group."
                />
              )}
            />
            <Controller
              name="description"
              control={editControl}
              render={({ field }) => (
                <InvTextField {...field} label="Description" multiline rows={2} />
              )}
            />
            <SwitchField name="is_active" label="Active" control={editControl} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" onClick={closeEditModal} disabled={editSaving}>
            Cancel
          </InvButton>
          <InvButton type="submit" form="edit-material-group-form" loading={editSaving}>
            Save Changes
          </InvButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate Material Group"
        description={`Deactivate "${deactivateTarget?.group_name}"? It will no longer be available for new items.`}
        destructive
        loading={deactivating}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />
    </MaterialMasterShell>
  );
}
